<template>
  <div>
    <p
      v-if="rows.length > 0"
      class="text-muted schedule-caption"
    >Monthly fee per tier, measured in elapsed months from the sharer's start (commencement{{ started ? ' — ' + formatDate(started) : '' }}) — not calendar dates and not handover. Each row is that tier's own monthly rate, not a running total.</p>

    <el-alert
      v-if="errored"
      type="error"
      :closable="false"
      show-icon
      title="Couldn't load the fee schedule"
      description="Please try again in a moment." />

    <el-row v-else-if="isEmpty">
      <el-col :span="24" align="center">
        <i class="fa fa-calendar-times text-muted"></i> <i class="text-muted">No fee schedule — this sharer has no rate set yet.</i>
      </el-col>
    </el-row>

    <div v-else v-loading="loading">

      <!-- Summary strip — only when meta is present -->
      <el-row v-if="metaSummary" :gutter="16" class="schedule-summary">
        <el-col :xs="24" :sm="8" class="schedule-summary__stat">
          <div class="schedule-summary__label">Current month</div>
          <div class="schedule-summary__value">
            {{ metaSummary.monthLabel }}
            <el-tooltip
              v-if="metaSummary.anchor"
              :content="metaSummary.anchor === 'handover' ? 'Measured in elapsed months from handover, not commencement.' : 'Measured in elapsed months from the sharer\'s commencement date.'"
              placement="top">
              <i class="fa fa-info-circle text-muted" aria-label="How the elapsed month is measured"></i>
            </el-tooltip>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="schedule-summary__stat">
          <div class="schedule-summary__label">Active tier</div>
          <div class="schedule-summary__value">
            <el-tag
              v-if="metaSummary.activeTier != null"
              size="small"
              effect="light"
              type="success">T{{ metaSummary.activeTier }}</el-tag>
            <span v-else class="text-muted">{{ metaSummary.activeTierLabel }}</span>
          </div>
        </el-col>
        <el-col :xs="24" :sm="8" class="schedule-summary__stat">
          <div class="schedule-summary__label">Billing</div>
          <div class="schedule-summary__value">
            {{ metaSummary.modeLabel }}
            <div v-if="metaSummary.showPosition" class="schedule-summary__position">{{ metaSummary.positionLabel }}</div>
          </div>
        </el-col>
      </el-row>

      <el-table :data="displayRows" style="width:100%" :row-class-name="rowClassName">

        <el-table-column label="Tier" width="64" align="center">
          <template slot-scope="scope">
            <span
              class="tier-tag"
              :class="tierBadgeClass(scope.row.tierSeq)"
              :aria-label="tierBadgeClass(scope.row.tierSeq) === 'tier-1' ? 'T' + scope.row.tierSeq + ', current tier' : undefined">{{ tierLabel(scope.row.tierSeq) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Period" min-width="120">
          <template slot-scope="scope">
            <el-tag
              v-if="isActiveRow(scope.row.tierSeq)"
              size="mini"
              type="success"
              effect="light">Current</el-tag>
            {{ scope.row.periodLabel }}
          </template>
        </el-table-column>

        <el-table-column min-width="150">
          <template #header>
            <span>Months (elapsed) <el-tooltip placement="top" content="Elapsed months since the sharer's start date. The end month is exclusive — this tier applies up to, but not including, the end month."><i class="fa fa-info-circle text-muted" aria-label="Month range explanation"></i></el-tooltip></span>
          </template>
          <template slot-scope="scope">
            {{ scope.row.monthLabel }}
          </template>
        </el-table-column>

        <el-table-column label="Monthly Fee" min-width="120" align="right" class-name="fee-cell">
          <template slot-scope="scope">
            {{ scope.row.feeLabel }}
          </template>
        </el-table-column>

      </el-table>
    </div>
  </div>
</template>

<script>
import NumberMixins from '@/mixins/NumberMixins'
import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'

export default {
  name: 'sharer-fee-schedule',

  mixins: [NumberMixins, PostMixins, DateMixins],

  props: {
    id: {
      type: [Number, String],
      required: true
    },
    started: {
      type: String,
      default: null
    }
  },

  data: function () {
    return {
      rows: [],
      meta: null,
      loading: false,
      loaded: false,
      errored: false
    }
  },

  computed: {
    displayRows: function () {
      var self = this
      return self.rows.map(function (row) {
        var periodLabel
        if (row.fromYear === row.toYear) {
          periodLabel = 'Year ' + row.fromYear
        } else {
          periodLabel = 'Year ' + row.fromYear + '–' + row.toYear
        }

        var monthLabel = row.fromMonth + '–' + row.toMonth

        var feeLabel
        if (row.fee === null || row.fee === undefined) {
          feeLabel = '—'
        } else {
          feeLabel = self.formatMoney(row.fee, 2)
        }

        return {
          tierSeq: row.tierSeq,
          periodLabel: periodLabel,
          monthLabel: monthLabel,
          feeLabel: feeLabel
        }
      })
    },

    isEmpty: function () {
      return this.loaded && !this.errored && this.rows.length === 0
    },

    activeTierSeq: function () {
      return this.meta && this.meta.activeTierSeq != null ? this.meta.activeTierSeq : null
    },

    hasActive: function () {
      return this.activeTierSeq != null
    },

    metaSummary: function () {
      if (!this.meta) {
        return null
      }

      var self = this
      var meta = self.meta
      var overridden = !!meta.overridden

      var monthLabel = meta.elapsedMonth != null ? String(meta.elapsedMonth) : 'Not started yet'

      var activeTier = self.activeTierSeq
      var activeTierLabel
      if (overridden) {
        // XOL-355: an overridden sharer bills a flat rate — no tier is "active".
        // Render the em-dash the Fee Timeline / ledger uses for null fields.
        activeTierLabel = '—'
      } else if (activeTier != null) {
        activeTierLabel = 'T' + activeTier
      } else if (meta.elapsedMonth != null) {
        activeTierLabel = 'Past schedule'
      } else {
        activeTierLabel = '—'
      }

      var modeLabel = self.billingModeLabel(meta.billingMode)
      var isQty = !overridden && String(meta.billingMode).toLowerCase() === 'qty'

      var positionLabel
      var showPosition
      if (overridden) {
        // XOL-355: drop the by-sharer Position/Count line; surface the flat
        // override rate instead when the backend supplies it.
        showPosition = meta.overrideRate != null
        positionLabel = meta.overrideRate != null ? 'Override rate: ' + self.formatMoney(meta.overrideRate, 2) : ''
      } else {
        showPosition = true
        if (isQty) {
          positionLabel = 'Count: ' + (meta.position != null ? meta.position : '—')
        } else {
          positionLabel = 'Position: ' + (meta.position != null ? meta.position : '—')
        }
      }

      return {
        overridden: overridden,
        monthLabel: monthLabel,
        anchor: meta.anchor || null,
        activeTier: activeTier,
        activeTierLabel: activeTierLabel,
        modeLabel: modeLabel,
        isQty: isQty,
        showPosition: showPosition,
        positionLabel: positionLabel
      }
    }
  },

  created: function () {
    this.load()
  },

  methods: {
    load: function () {
      if (!this.id) {
        this.loaded = true
        return
      }

      var self = this
      this.loading = true
      this.errored = false
      this.loaded = false

      this.fetchData(
        'licensees/' + this.id + '/fee-schedule',
        function (data) {
          if (Array.isArray(data)) {
            self.rows = data
            self.meta = null
          } else if (data && typeof data === 'object') {
            self.rows = Array.isArray(data.rows) ? data.rows : []
            self.meta = (data.meta && typeof data.meta === 'object') ? data.meta : null
          } else {
            self.rows = []
            self.meta = null
          }
          self.loading = false
          self.loaded = true
        },
        'fee schedule',
        function (_err) {
          self.errored = true
          self.loading = false
          self.loaded = true
        }
      )
    },

    // XOL-355: single source of truth for "is this row the active tier". The
    // hasActive guard stops the override flat row (tierSeq null) from matching
    // activeTierSeq null — null === null would otherwise read as active and
    // wrongly tint the row / show the Current tag.
    isActiveRow: function (tierSeq) {
      return this.hasActive && tierSeq === this.activeTierSeq
    },

    rowClassName: function (obj) {
      return this.isActiveRow(obj.row.tierSeq) ? 'tier-row--active' : ''
    },

    tierBadgeClass: function (tierSeq) {
      return this.isActiveRow(tierSeq) ? 'tier-1' : 'tier-n'
    },

    // XOL-355: the override flat row carries tierSeq null — render an em-dash
    // instead of a bare "T".
    tierLabel: function (tierSeq) {
      return tierSeq != null ? 'T' + tierSeq : '—'
    },

    billingModeLabel: function (mode) {
      var m = String(mode).toLowerCase()
      if (m === 'overridden') {
        return 'Rate overridden'
      }
      return m === 'qty' ? 'Billed by sharer Qty' : 'Billed by sharer No.'
    }
  }
}
</script>

<style scoped>
/* Tier tag tokens — copied from RateTiersCell.vue */
.tier-tag {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
}

.tier-1 {
  background: #f0f9eb;
  color: #67c23a;
}

.tier-n {
  background: #f4f4f5;
  color: #909399;
}

::v-deep .fee-cell {
  font-variant-numeric: tabular-nums;
}

.schedule-caption {
  font-size: 12px;
}

/* Active row highlight */
::v-deep .tier-row--active td {
  background: #f0f9eb;
}

/* Summary strip */
.schedule-summary {
  margin-bottom: 8px;
}

.schedule-summary__stat {
  padding: 8px 0;
}

.schedule-summary__label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 2px;
}

.schedule-summary__value {
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}

.schedule-summary__position {
  font-size: 12px;
  color: #909399;
}
</style>
